/*
 * <pb-ugc-videos>: vídeos de clientes.
 * - Trilho: cada vídeo toca sem som só enquanto está visível (e nunca com "reduzir movimento").
 * - Visualizador (<dialog>): abre no vídeo tocado, com som, barra de progresso, anterior/próximo
 *   (setas, teclado e deslize lateral); ao terminar um vídeo passa para o seguinte.
 */
if (!customElements.get('pb-ugc-videos')) {
  customElements.define(
    'pb-ugc-videos',
    class PbUgcVideos extends HTMLElement {
      connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('[data-open]'));
        this.dialog = this.querySelector('.pb-ugc__viewer');
        this.slides = Array.from(this.querySelectorAll('.pb-ugc__slide'));
        this.bars = Array.from(this.querySelectorAll('.pb-ugc__bar > span'));
        this.soundButton = this.querySelector('[data-sound]');
        this.muted = false;
        this.index = 0;

        this.cards.forEach((card) => card.addEventListener('click', () => this.open(Number(card.dataset.open), card)));
        this.setupPreview();
        if (!this.dialog) return;

        this.dialog.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
        this.dialog.querySelectorAll('[data-dir]').forEach((button) =>
          button.addEventListener('click', () => this.go(Number(button.dataset.dir)))
        );
        this.soundButton?.addEventListener('click', () => this.setMuted(!this.muted));
        this.dialog.addEventListener('close', () => this.onClose());
        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.go(1);
          if (event.key === 'ArrowLeft') this.go(-1);
        });
        // clique no fundo (fora do vídeo) fecha, como nos stories
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });
        // a compra abre a gaveta do carrinho: fecha o visualizador para ela aparecer
        this.dialog.addEventListener('submit', () => this.dialog.close());
        this.setupSwipe();

        this.slides.forEach((slide, i) => {
          const video = slide.querySelector('video');
          if (!video) return;
          video.addEventListener('timeupdate', () => {
            if (i === this.index && video.duration) this.setBar(i, video.currentTime / video.duration);
          });
          video.addEventListener('ended', () => (i < this.slides.length - 1 ? this.go(1) : this.setBar(i, 1)));
          // toque no vídeo pausa/retoma (não depois de um deslize)
          video.addEventListener('click', () => {
            if (i !== this.index || this.swiped) return;
            video.paused ? video.play().catch(() => {}) : video.pause();
          });
        });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      setupPreview() {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const previews = this.cards.map((card) => card.querySelector('video')).filter(Boolean);
        if (reduce || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(({ target, isIntersecting }) => {
              if (isIntersecting && !this.dialog?.open) {
                target.muted = true;
                target.play().catch(() => {});
              } else {
                target.pause();
              }
            });
          },
          { threshold: 0.6 }
        );
        previews.forEach((video) => this.observer.observe(video));
      }

      setupSwipe() {
        const area = this.dialog.querySelector('.pb-ugc__slides');
        if (!area) return;
        let startX = null;
        let startY = null;
        area.addEventListener('pointerdown', (event) => {
          startX = event.clientX;
          startY = event.clientY;
          this.swiped = false;
        });
        area.addEventListener('pointerup', (event) => {
          if (startX === null) return;
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          startX = null;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
            this.swiped = true;
            this.go(dx < 0 ? 1 : -1);
          } else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) {
            this.swiped = true;
            this.dialog.close();
          }
        });
        area.addEventListener('pointercancel', () => (startX = null));
      }

      open(index, opener) {
        if (!this.dialog) return;
        this.opener = opener;
        this.cards.forEach((card) => card.querySelector('video')?.pause());
        this.dialog.showModal();
        document.body.classList.add('overflow-hidden');
        this.show(index);
      }

      onClose() {
        this.slides.forEach((slide) => slide.querySelector('video')?.pause());
        document.body.classList.remove('overflow-hidden');
        // volta a tocar as prévias visíveis
        if (this.observer) {
          this.cards.forEach((card) => {
            const video = card.querySelector('video');
            if (video) {
              this.observer.unobserve(video);
              this.observer.observe(video);
            }
          });
        }
        this.opener?.focus();
      }

      go(step) {
        const next = this.index + step;
        if (next < 0 || next >= this.slides.length) return;
        this.show(next);
      }

      show(index) {
        this.index = index;
        this.slides.forEach((slide, i) => {
          const active = i === index;
          slide.hidden = !active;
          const video = slide.querySelector('video');
          if (!video) return;
          if (active) {
            video.currentTime = 0;
            video.muted = this.muted;
            video.play().catch(() => {
              // o navegador pode bloquear som sem gesto: toca mudo
              this.setMuted(true);
              video.play().catch(() => {});
            });
          } else {
            video.pause();
          }
          this.setBar(i, i < index ? 1 : 0);
        });
        this.dialog.querySelector('[data-dir="-1"]')?.toggleAttribute('disabled', index === 0);
        this.dialog.querySelector('[data-dir="1"]')?.toggleAttribute('disabled', index === this.slides.length - 1);
        this.updateSoundButton();
      }

      setBar(i, progress) {
        this.bars[i]?.style.setProperty('--p', String(Math.min(1, Math.max(0, progress))));
      }

      setMuted(muted) {
        this.muted = muted;
        const video = this.slides[this.index]?.querySelector('video');
        if (video) video.muted = muted;
        this.updateSoundButton();
      }

      updateSoundButton() {
        if (!this.soundButton) return;
        this.soundButton.setAttribute('aria-pressed', String(!this.muted));
      }
    }
  );
}
